import {
  Content,
  fetchOneEntry,
  getBuilderSearchParams,
} from "@builder.io/sdk-react/edge";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { CUSTOM_COMPONENTS } from "~/components/builder-registry";
import { createBrandMeta } from "~/lib/shared/meta";
import { clientEnv } from "~/lib/utils";
import { z } from "zod";

const BUILDER_MODEL = "page";
const SUMMIT_URL_PATH = "/page/summit";

const BuilderPageDataSchema = z
  .object({
    title: z.string().optional(),
    name: z.string().optional(),
    description: z.string().optional(),
    excerpt: z.string().optional(),
    image: z.string().optional(),
    heroImage: z.string().optional(),
    keywords: z.array(z.string()).optional(),
    tags: z.array(z.string()).optional(),
  })
  .passthrough();

async function fetchSummitContent(search: URLSearchParams) {
  return fetchOneEntry({
    model: BUILDER_MODEL,
    apiKey: clientEnv.VITE_BUILDER_API_KEY,
    userAttributes: { urlPath: SUMMIT_URL_PATH },
    includeUnpublished: false,
    options: getBuilderSearchParams(search),
  });
}

export const Route = createFileRoute("/summit/")({
  loader: async ({ context, location }) => {
    const builderContent = await fetchSummitContent(
      new URLSearchParams(location.search),
    );

    if (!builderContent) {
      throw notFound();
    }

    await context.queryClient.ensureQueryData({
      queryKey: ["builder-content", SUMMIT_URL_PATH],
      queryFn: () => builderContent,
      staleTime: 1000 * 60 * 3,
    });

    return { content: builderContent };
  },
  head: ({ loaderData }) => {
    const content = loaderData?.content ?? null;
    const parsedPageData = BuilderPageDataSchema.safeParse(content?.data);
    const pageData = parsedPageData.success ? parsedPageData.data : null;

    if (!pageData) {
      return createBrandMeta({
        title: "Dream Coach Match Summit",
        description:
          "Register for the Dream Coach Match Summit to learn from top coaches and grow your practice.",
        ogType: "website",
        canonical: "https://dreamcoachmatch.com/summit",
        ogUrl: "https://dreamcoachmatch.com/summit",
      });
    }

    const title = pageData.title || pageData.name || "Dream Coach Match Summit";
    const description =
      pageData.description ||
      pageData.excerpt ||
      "Register for the Dream Coach Match Summit to learn from top coaches and grow your practice.";
    const image = pageData.image || pageData.heroImage;
    const url = "https://dreamcoachmatch.com/summit";

    return createBrandMeta({
      title,
      description,
      ogImage: image,
      ogUrl: url,
      canonical: url,
      keywords: pageData.keywords || pageData.tags,
      ogType: "website",
    });
  },
  component: SummitLandingPage,
});

function SummitLandingPage() {
  const { data: content } = useQuery({
    queryKey: ["builder-content", SUMMIT_URL_PATH],
    queryFn: () => fetchSummitContent(new URLSearchParams(location.search)),
    staleTime: 1000 * 60 * 3,
  });

  return (
    <Content
      model={BUILDER_MODEL}
      content={content}
      apiKey={clientEnv.VITE_BUILDER_API_KEY}
      customComponents={CUSTOM_COMPONENTS}
    />
  );
}
